import Menu from "@/core/data/menu.json";

type MenuNode = {
  title?: string;
  headTitle1?: string;
  path?: string;
  matchPaths?: string[];
  /** Screen name shown after the menu trail for a matched child route. */
  matchTitles?: Record<string, string>;
  children?: MenuNode[];
};

export type SidebarTrail = {
  labels: string[];
  /** True when the route is a record opened from a sidebar screen. */
  detail: boolean;
};

type Candidate = SidebarTrail & { score: number };

function normalizePath(path: string): string {
  if (!path) return "";
  let value = path.split("#")[0];
  // Keep `:param?` optional markers. Strip a real query string.
  value = value.replace(/:([^/?]+)\?/g, ":$1__OPTIONAL__");
  value = value.split("?")[0];
  value = value.replace(/__OPTIONAL__/g, "?");
  if (!value.startsWith("/")) value = `/${value}`;
  if (value.length > 1 && value.endsWith("/")) value = value.slice(0, -1);
  return value.toLowerCase();
}

/** True when a menu path or matchPath pattern matches the current address. */
export function menuPathMatches(pattern: string, path: string): boolean {
  if (!pattern || !path) return false;
  if (!pattern.includes(":")) {
    const base = normalizePath(pattern);
    const current = normalizePath(path);
    return current === base || current.startsWith(`${base}/`);
  }
  return patternMatches(pattern, path);
}

function segments(path: string): string[] {
  return normalizePath(path).split("/").filter(Boolean);
}

function staticCount(path: string): number {
  return segments(path).filter((part) => !part.startsWith(":")).length;
}

function paramCount(pattern: string): number {
  return segments(pattern).filter((part) => part.startsWith(":")).length;
}

/** Sidebar-style pattern. A trailing `:param?` segment is optional. */
function patternMatches(pattern: string, path: string): boolean {
  const patternParts = segments(pattern);
  const pathParts = segments(path);
  let pathIndex = 0;

  for (let i = 0; i < patternParts.length; i++) {
    const part = patternParts[i];
    const isParam = part.startsWith(":");

    if (pathIndex >= pathParts.length) {
      return patternParts.slice(i).every((rest) => rest.startsWith(":") && rest.endsWith("?"));
    }

    if (isParam) {
      pathIndex++;
      continue;
    }

    if (part !== pathParts[pathIndex]) return false;
    pathIndex++;
  }

  return pathIndex === pathParts.length;
}

function isSegmentPrefix(menuPath: string, currentPath: string): boolean {
  const base = normalizePath(menuPath);
  const path = normalizePath(currentPath);
  if (!base || base === "/") return false;
  return path.startsWith(`${base}/`);
}

function consider(best: Candidate | null, candidate: Candidate): Candidate {
  if (
    !best ||
    candidate.score > best.score ||
    (candidate.score === best.score && candidate.labels.length > best.labels.length)
  ) {
    return candidate;
  }
  return best;
}

function walk(nodes: MenuNode[] | undefined, trail: string[], currentPath: string, best: Candidate | null): Candidate | null {
  for (const node of nodes || []) {
    const label = node.title || node.headTitle1;
    const next = label ? trail.concat(label) : trail;

    if (node.path && normalizePath(node.path) === normalizePath(currentPath)) {
      best = consider(best, {
        labels: next,
        detail: false,
        score: 1_000_000 + staticCount(node.path),
      });
    }

    if (node.path && isSegmentPrefix(node.path, currentPath)) {
      best = consider(best, {
        labels: next,
        detail: true,
        score: staticCount(node.path) * 100,
      });
    }

    for (const pattern of node.matchPaths || []) {
      if (!patternMatches(pattern, currentPath)) continue;
      const screen = node.matchTitles?.[pattern];
      const hasParam = segments(pattern).some((part) => part.startsWith(":"));
      best = consider(best, {
        labels: screen ? next.concat(screen) : next,
        detail: hasParam,
        score: staticCount(pattern) * 100 - paramCount(pattern),
      });
    }

    best = walk(node.children, next, currentPath, best);
  }
  return best;
}

/** Sidebar labels for the current address, including screens opened from a menu item. */
export function resolveSidebarTrail(currentPath: string): SidebarTrail | null {
  const match = walk((Menu as { data: MenuNode[] }).data, [], currentPath, null);
  if (!match || !match.labels.length) return null;
  return { labels: match.labels, detail: match.detail };
}

/** Sidebar labels for an exact menu link. Detail routes return null. */
export function findSidebarTrail(currentPath: string): string[] | null {
  const match = resolveSidebarTrail(currentPath);
  if (!match || match.detail) return null;
  return match.labels;
}

/** Last breadcrumb item for a record opened from a sidebar screen. */
export function setBreadcrumbEntity(name?: string | null) {
  if (typeof window === "undefined") return;
  const entityName = String(name || "").replace(/\s+/g, " ").trim();
  window.dispatchEvent(
    new CustomEvent("breadcrumb-update", {
      detail: {
        entityName,
        entityPath: window.location.pathname,
      },
    })
  );
}
